/**
 * Envelope normalization + wire-query builders (merchant `merchant-mappers.ts`
 * parity — same gateway `{list, meta}` envelope, same per-item `safeParse` so
 * one malformed row never nukes the whole page).
 */
import {
  DEFAULT_SYSTEM_USER_SEARCH_TYPE,
  systemUserSchema,
  systemUserListEnvelopeSchema,
  systemUserSearchTypeSchema,
  systemUserSessionSchema,
  systemUserSessionListEnvelopeSchema,
  type SystemUser,
  type SystemUserList,
  type SystemUserSessionList,
  type SystemUserSearchType,
} from '../model/schema';

export interface SystemUserListParams {
  searchType?: SystemUserSearchType | undefined;
  searchValue?: string | undefined;
  role?: string | undefined;
  status?: string | undefined;
  limit?: number | undefined;
  offset?: number | undefined;
}

/**
 * Served `search_type` for a raw URL value. The URL schema lets unknown
 * types through (never crash route validation on hand-made links); anything
 * unserved falls back to the default column so the wire only ever carries a
 * served `search_type`.
 */
export function resolveSystemUserSearchType(value: unknown): SystemUserSearchType {
  const parsed = systemUserSearchTypeSchema.safeParse(value);
  return parsed.success ? parsed.data : DEFAULT_SYSTEM_USER_SEARCH_TYPE;
}

/** `limit`/`offset` page params plus the optional search/role/status filters. */
export function buildSystemUserListQuery(
  params: SystemUserListParams,
): Record<string, string> {
  const query: Record<string, string> = {};
  if (params.limit !== undefined) query['pagination.limit'] = String(params.limit);
  if (params.offset !== undefined) query['pagination.offset'] = String(params.offset);
  if (
    params.searchType !== undefined &&
    params.searchValue !== undefined &&
    params.searchValue !== ''
  ) {
    query.search_type = params.searchType;
    query.search_value = params.searchValue;
  }
  if (params.role !== undefined && params.role !== '') query.role = params.role;
  if (params.status !== undefined && params.status !== '') query.status = params.status;
  return query;
}

/** Parse one identity card; `undefined` when the row fails the contract. */
export function toSystemUser(body: unknown): SystemUser | undefined {
  const parsed = systemUserSchema.safeParse(body);
  return parsed.success ? parsed.data : undefined;
}

/** Adapt the gateway list envelope into the table-friendly domain model. */
export function toSystemUserList(body: unknown): SystemUserList {
  const parsed = systemUserListEnvelopeSchema.safeParse(body);
  if (!parsed.success) return { list: [] };
  const list = (parsed.data.list ?? [])
    .map(toSystemUser)
    .filter((u): u is SystemUser => Boolean(u));
  const meta = parsed.data.meta ?? undefined;
  return meta ? { list, meta } : { list };
}

/** Adapt the sessions list envelope the same way. */
export function toSystemUserSessionList(body: unknown): SystemUserSessionList {
  const parsed = systemUserSessionListEnvelopeSchema.safeParse(body);
  if (!parsed.success) return { list: [] };
  const list = (parsed.data.list ?? [])
    .map((item) => systemUserSessionSchema.safeParse(item).data)
    .filter((s) => s !== undefined);
  const meta = parsed.data.meta ?? undefined;
  return meta ? { list, meta } : { list };
}

/**
 * Page count for the table pagination: prefer the backend's own `page_count`,
 * fall back to `ceil(total / limit)`.
 */
export function toSystemUserPageCount(
  response: SystemUserList | undefined,
  limit: number,
): number {
  const pageCount = response?.meta?.page_count ?? undefined;
  if (typeof pageCount === 'number' && pageCount > 0) return pageCount;
  const total = response?.meta?.total ?? 0;
  return Math.max(1, Math.ceil(total / Math.max(1, limit)));
}

interface AxiosErrorLike {
  response?: {
    status?: number;
    data?: { error?: { code?: unknown; message?: string; details?: unknown } };
  };
}

interface ApiErrorShape {
  status?: number;
  code?: unknown;
  details?: unknown;
  message?: string;
}

/**
 * Surfaces the service's own message where it has one — field-level validation
 * (a malformed phone, an unknown role) is far more useful than a generic
 * failure string. Merchant `merchantErrorMessage` parity, minus its catalog of
 * hardcoded strings: both wordings are injected so the feature can translate.
 *
 * Accepts both the legacy axios shape (`error.response.data.error`) and the
 * new `ApiError` shape — the http client normalizes the same
 * `{error:{code,message,details}}` envelope into both.
 */
export function systemUserErrorMessage(
  err: unknown,
  messages: { fallback: string; forbidden: string },
): string {
  const axiosRes = (err as AxiosErrorLike)?.response;
  const axiosBody = axiosRes?.data?.error;

  const shape = err as ApiErrorShape;
  const status =
    axiosRes?.status ?? (typeof shape?.status === 'number' ? shape.status : undefined);
  const rawDetails = axiosBody?.details ?? shape?.details;
  const detail = Array.isArray(rawDetails)
    ? (rawDetails[0] as { field?: string; issue?: string } | undefined)
    : undefined;
  const message =
    axiosBody?.message ??
    (typeof shape?.message === 'string' ? shape.message : undefined);

  if (detail?.field && detail?.issue) return `${detail.field}: ${detail.issue}`;
  if (status === 403) return messages.forbidden;
  if (message === undefined || message === '') return messages.fallback;
  return message;
}
